import styled from 'styled-components';
import type { FinPaymentRegistryRow } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  IconRefresh,
  IconFileImport,
  IconArrowLeftRight,
  IconFileSpreadsheet,
  IconPlus,
} from '@shared/ui/icons';
import { formatDateRu, formatMoney } from '../financierFormat';
import {
  FilterBar,
  RegistrySelectBox,
  RegistrySelectLabel,
  RegistrySelect,
  ActionsGroup,
  SearchInput,
  Select,
} from './styled';

// Поиск реестра на сервере и подпись «показаны первые 100 из N».
const RegistrySearch = styled.input`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  flex: 0 1 220px;
  min-width: 0;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const RegistryHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface RequestsFilterToolbarProps {
  selectedRegistryId: string;
  onSelectRegistry: (id: string) => void;
  registries: FinPaymentRegistryRow[];
  /** Поиск реестра на сервере по номеру или названию. */
  registrySearch?: string;
  onRegistrySearchChange?: (val: string) => void;
  /** «Показаны первые 100 из N…»; `null` — показаны все. */
  registriesHint?: string | null;
  onSync1C: () => void;
  isSyncing1C: boolean;
  onOpenImport: () => void;
  onOpenReconcile: () => void;
  onDownloadExcel: () => void;
  onOpenCreateRegistry: () => void;
  search: string;
  onSearchChange: (val: string) => void;
  statusFilter: string;
  onStatusFilterChange: (val: string) => void;
  moduleFilter: string;
  onModuleFilterChange: (val: string) => void;
  /** Сколько в списке заявок сотрудников (`source_module = 'staff'`); `null` — список не загружен. */
  staffCount: number | null;
  canWrite: boolean;
}

export function RequestsFilterToolbar({
  selectedRegistryId,
  onSelectRegistry,
  registries,
  registrySearch,
  onRegistrySearchChange,
  registriesHint,
  onSync1C,
  isSyncing1C,
  onOpenImport,
  onOpenReconcile,
  onDownloadExcel,
  onOpenCreateRegistry,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  moduleFilter,
  onModuleFilterChange,
  staffCount,
  canWrite,
}: RequestsFilterToolbarProps) {
  const staffOnly = moduleFilter === 'staff';
  return (
    <>
      <FilterBar>
        <RegistrySelectBox>
          <RegistrySelectLabel>Реестр:</RegistrySelectLabel>
          <RegistrySelect
            value={selectedRegistryId}
            onChange={(e) => onSelectRegistry(e.target.value)}
          >
            <option value="all">Все реестры (Сводный вид)</option>
            {registries.map((reg) => (
              <option key={reg.id} value={reg.id}>
                {reg.registry_number} — {reg.title} ({formatDateRu(reg.registry_date)}) ·{' '}
                {formatMoney(reg.total_approved_amount || reg.total_requested_amount)}
              </option>
            ))}
          </RegistrySelect>
        </RegistrySelectBox>

        {onRegistrySearchChange && (
          <RegistrySearch
            type="search"
            value={registrySearch ?? ''}
            onChange={(e) => onRegistrySearchChange(e.target.value)}
            placeholder="Найти реестр по номеру или названию"
            aria-label="Найти реестр по номеру или названию"
          />
        )}
        {registriesHint && <RegistryHint role="status">{registriesHint}</RegistryHint>}

        <ActionsGroup>
          <Button
            $variant="secondary"
            $size="sm"
            onClick={onSync1C}
            disabled={isSyncing1C || !canWrite}
          >
            <IconRefresh size={14} />
            {isSyncing1C ? '1С API…' : '1С (API)'}
          </Button>

          <Button
            $variant="secondary"
            $size="sm"
            onClick={onOpenImport}
            disabled={!canWrite}
          >
            <IconFileImport size={14} />
            Импорт Excel/1С
          </Button>

          <Button
            $variant="secondary"
            $size="sm"
            onClick={onOpenReconcile}
            disabled={!canWrite}
          >
            <IconArrowLeftRight size={14} />
            Сверка с выпиской
          </Button>

          <Button
            $variant="secondary"
            $size="sm"
            onClick={onDownloadExcel}
          >
            <IconFileSpreadsheet size={14} />
            Скачать Excel (.csv)
          </Button>

          <Button
            $variant="secondary"
            $size="sm"
            onClick={onOpenCreateRegistry}
            disabled={!canWrite}
          >
            <IconPlus size={14} />
            Создать реестр
          </Button>
        </ActionsGroup>
      </FilterBar>

      <FilterBar>
        <SearchInput
          placeholder="Поиск по № заявки, контрагенту, ИНН, назначению, номеру платёжки ПП…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <Select value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value)}>
          <option value="all">Все статусы заявок</option>
          <option value="draft">Черновик</option>
          <option value="in_review">На финконтроле</option>
          <option value="ready_for_ceo">Готово к Гендиректору</option>
          <option value="on_ceo_approval">На акцепте у Гендиректора</option>
          <option value="approved">Акцептовано</option>
          <option value="exported_1c">Выгружено в 1С</option>
          <option value="executed">Исполнено банком</option>
          <option value="deferred">Отложено</option>
          <option value="rejected">Отклонено</option>
        </Select>

        <Select value={moduleFilter} onChange={(e) => onModuleFilterChange(e.target.value)}>
          <option value="all">Все модули-источники</option>
          <option value="mto">МТО / Снабжение</option>
          <option value="logist">Логистика</option>
          <option value="smeta">Сметчик (Субподряд КС-3)</option>
          <option value="chronum_payroll">Хронум (ФОТ/Зарплата)</option>
          <option value="taxes_ens">Налоги (ЕНС/НДС)</option>
          <option value="debt_leasing">Кредиты и лизинг</option>
          <option value="manual">Ручной ввод</option>
          <option value="staff">Заявки сотрудников</option>
        </Select>

        {/* Заявки сотрудников тонут среди собранных из цехов: список идёт по сроку оплаты. */}
        <Button
          $variant={staffOnly ? 'primary' : 'secondary'}
          $size="sm"
          aria-pressed={staffOnly}
          onClick={() => onModuleFilterChange(staffOnly ? 'all' : 'staff')}
          title={
            staffOnly
              ? 'Показать заявки всех модулей'
              : 'Показать только заявки на оплату от сотрудников'
          }
        >
          Заявки сотрудников{staffCount !== null ? ` (${staffCount})` : ''}
        </Button>
      </FilterBar>
    </>
  );
};
